import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';

import { 
  BarChart3, 
  Mail, 
  TrendingUp, 
    Eye,
  MousePointer,
  Download
} from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'react-hot-toast';

interface EmailMetrics {
  totalSent: number;
  totalOpened: number;
  totalClicked: number;
  totalReplied: number;
  openRate: number;
  clickRate: number;
  replyRate: number;
  averageOpenTime: number;
}

interface EmailCampaign {
  id: string;
  name: string;
  templateName: string;
  sentCount: number;
  openedCount: number;
  clickedCount: number;
  repliedCount: number;
  openRate: number;
  clickRate: number;
  replyRate: number;
  sentDate: string;
  status: 'completed' | 'scheduled' | 'draft';
}

interface EmailTemplate {
  id: string;
  name: string;
  category: string;
  totalSent: number;
  averageOpenRate: number;
  averageClickRate: number;
}

interface EmailRecipient {
  id: string;
  email: string;
  name: string;
  sentDate: string;
  openedDate?: string;
  clickedDate?: string;
  repliedDate?: string;
  openCount: number;
  clickCount: number;
  status: 'sent' | 'opened' | 'clicked' | 'replied' | 'bounced';
}

export const EmailReportsPage: React.FC = () => {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState<EmailMetrics | null>(null);
  const [campaigns, setCampaigns] = useState<EmailCampaign[]>([]);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [recipients, setRecipients] = useState<EmailRecipient[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30'); // days
  const [selectedCampaign, setSelectedCampaign] = useState<string>('');
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');

  const dateRanges = [
    { value: '7', label: 'Últimos 7 dias' },
    { value: '30', label: 'Últimos 30 dias' },
    { value: '90', label: 'Últimos 90 dias' },
    { value: '365', label: 'Último ano' },
  ];

  const loadMetrics = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/email-reports/metrics?days=${dateRange}`);
      setMetrics(response.data.data);
    } catch (error) {
      toast.error('Erro ao carregar métricas');
    } finally {
      setLoading(false);
    }
  };

  const loadCampaigns = async () => {
    try {
      const response = await api.get(`/email-reports/campaigns?days=${dateRange}`);
      setCampaigns(response.data.data);
    } catch (error) {
      toast.error('Erro ao carregar campanhas');
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await api.get(`/email-reports/templates?days=${dateRange}`);
      setTemplates(response.data.data);
    } catch (error) {
      toast.error('Erro ao carregar templates');
    }
  };

  const loadRecipients = async () => {
    try {
      const params = new URLSearchParams();
      if (dateRange) params.append('days', dateRange);
      if (selectedCampaign) params.append('campaignId', selectedCampaign);
      if (selectedTemplate) params.append('templateId', selectedTemplate);
      
      const response = await api.get(`/email-reports/recipients?${params}`);
      setRecipients(response.data.data);
    } catch (error) {
      toast.error('Erro ao carregar destinatários');
    }
  };

  useEffect(() => {
    loadMetrics();
    loadCampaigns();
    loadTemplates();
  }, [dateRange]);

  useEffect(() => {
    loadRecipients();
  }, [dateRange, selectedCampaign, selectedTemplate]);

  const exportReport = async (type: 'campaigns' | 'templates' | 'recipients') => {
    try {
      const params = new URLSearchParams();
      if (dateRange) params.append('days', dateRange);
      if (selectedCampaign) params.append('campaignId', selectedCampaign);
      if (selectedTemplate) params.append('templateId', selectedTemplate);
      
      const response = await api.get(`/email-reports/export/${type}?${params}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `email-report-${type}-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Relatório exportado com sucesso');
    } catch (error) {
      toast.error('Erro ao exportar relatório');
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      completed: 'default',
      scheduled: 'secondary',
      draft: 'outline',
      sent: 'default',
      opened: 'default',
      clicked: 'default',
      replied: 'default',
      bounced: 'destructive',
    };
    
    return (
      <Badge variant={(variants[status as keyof typeof variants] as any) || 'secondary'}>
        {status}
      </Badge>
    );
  };

  const formatPercentage = (value: number) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  // const formatTime = (minutes: number) => {
  //   if (minutes < 60) return `${minutes}min`;
  //   const hours = Math.floor(minutes / 60);
  //   const mins = minutes % 60;
  //   return `${hours}h ${mins}min`;
  // };

  return (
    <div className="container mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-slate-100">Relatórios de Email</h1>
          <p className="text-gray-600 dark:text-slate-300">Métricas e análises de performance dos emails</p>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" onClick={() => navigate('/configuracoes')}>
            ← Voltar para Configurações &gt; E-mails
          </Button>
          <Select
            value={dateRange}
            onValueChange={(value) => setDateRange(value)}
          >
            {dateRanges.map((range) => (
              <option key={range.value} value={range.value}>
                {range.label}
              </option>
            ))}
          </Select>
          <Button variant="outline" onClick={() => exportReport('campaigns')}>
            <Download className="w-4 h-4 mr-2" />
            Exportar
          </Button>
        </div>
      </div>

      {/* Metrics Cards */}
      {metrics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Enviados</CardTitle>
              <Mail className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metrics.totalSent.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">
                Emails enviados no período
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Taxa de Abertura</CardTitle>
              <Eye className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatPercentage(metrics.openRate)}</div>
              <p className="text-xs text-muted-foreground">
                {metrics.totalOpened.toLocaleString()} aberturas
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Taxa de Clique</CardTitle>
              <MousePointer className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatPercentage(metrics.clickRate)}</div>
              <p className="text-xs text-muted-foreground">
                {metrics.totalClicked.toLocaleString()} cliques
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Taxa de Resposta</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatPercentage(metrics.replyRate)}</div>
              <p className="text-xs text-muted-foreground">
                {metrics.totalReplied.toLocaleString()} respostas
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs value="campaigns" onValueChange={() => {}} className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="campaigns" className="flex items-center">
            <BarChart3 className="w-4 h-4 mr-2" />
            Campanhas
          </TabsTrigger>
          <TabsTrigger value="templates" className="flex items-center">
            <Mail className="w-4 h-4 mr-2" />
            Templates
          </TabsTrigger>
          <TabsTrigger value="recipients" className="flex items-center">
            <Eye className="w-4 h-4 mr-2" />
            Destinatários
          </TabsTrigger>
          <TabsTrigger value="trends" className="flex items-center">
            <TrendingUp className="w-4 h-4 mr-2" />
            Tendências
          </TabsTrigger>
        </TabsList>

        <TabsContent value="campaigns" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Campanhas de Email</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">Carregando...</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Campanha</TableHead>
                      <TableHead>Template</TableHead>
                      <TableHead>Enviados</TableHead>
                      <TableHead>Aberturas</TableHead>
                      <TableHead>Cliques</TableHead>
                      <TableHead>Respostas</TableHead>
                      <TableHead>Taxa Abertura</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {campaigns.map((campaign) => (
                      <TableRow key={campaign.id}>
                        <TableCell className="font-medium">{campaign.name}</TableCell>
                        <TableCell>{campaign.templateName}</TableCell>
                        <TableCell>{campaign.sentCount.toLocaleString()}</TableCell>
                        <TableCell>{campaign.openedCount.toLocaleString()}</TableCell>
                        <TableCell>{campaign.clickedCount.toLocaleString()}</TableCell>
                        <TableCell>{campaign.repliedCount.toLocaleString()}</TableCell>
                        <TableCell>{formatPercentage(campaign.openRate)}</TableCell>
                        <TableCell>{getStatusBadge(campaign.status)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="templates" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Performance por Template</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Template</TableHead>
                    <TableHead>Categoria</TableHead>
                    <TableHead>Total Enviados</TableHead>
                    <TableHead>Taxa Média Abertura</TableHead>
                    <TableHead>Taxa Média Clique</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {templates.map((template) => (
                    <TableRow key={template.id}>
                      <TableCell className="font-medium">{template.name}</TableCell>
                      <TableCell>{template.category}</TableCell>
                      <TableCell>{template.totalSent.toLocaleString()}</TableCell>
                      <TableCell>{formatPercentage(template.averageOpenRate)}</TableCell>
                      <TableCell>{formatPercentage(template.averageClickRate)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="recipients" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Destinatários</CardTitle>
                <div className="flex space-x-2">
                  <Select
                    value={selectedCampaign}
                    onValueChange={(value) => setSelectedCampaign(value)}
                  >
                    <option value="">Todas as campanhas</option>
                    {campaigns.map((campaign) => (
                      <option key={campaign.id} value={campaign.id}>
                        {campaign.name}
                      </option>
                    ))}
                  </Select>
                  <Select
                    value={selectedTemplate}
                    onValueChange={(value) => setSelectedTemplate(value)}
                  >
                    <option value="">Todos os templates</option>
                    {templates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead>Enviado</TableHead>
                    <TableHead>Aberto</TableHead>
                    <TableHead>Clicado</TableHead>
                    <TableHead>Respondido</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recipients.map((recipient) => (
                    <TableRow key={recipient.id}>
                      <TableCell className="font-medium">{recipient.email}</TableCell>
                      <TableCell>{recipient.name}</TableCell>
                      <TableCell>{new Date(recipient.sentDate).toLocaleDateString()}</TableCell>
                      <TableCell>
                        {recipient.openedDate ? new Date(recipient.openedDate).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell>
                        {recipient.clickedDate ? new Date(recipient.clickedDate).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell>
                        {recipient.repliedDate ? new Date(recipient.repliedDate).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell>{getStatusBadge(recipient.status)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="trends" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Tendências de Performance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-medium mb-4">Taxa de Abertura ao Longo do Tempo</h3>
                  <div className="h-64 bg-gray-100 dark:bg-slate-700 rounded flex items-center justify-center">
                    <p className="text-gray-500 dark:text-slate-400">Gráfico em desenvolvimento...</p>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-medium mb-4">Taxa de Clique ao Longo do Tempo</h3>
                  <div className="h-64 bg-gray-100 dark:bg-slate-700 rounded flex items-center justify-center">
                    <p className="text-gray-500 dark:text-slate-400">Gráfico em desenvolvimento...</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
